import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { getAnalyticsApiBaseUrl } from '../config/api';
import { fetchWithAuth } from '../utils/auth';

interface MatchedUser {
  _id: string;
  Email: string;
  FacebookID: string;
  AccountStatus: string;
  UserName: string;
  FullName: string;
}

interface LookupResult {
  matched: MatchedUser[];
  duplicates: { facebookId: string; count: number; users: any[] }[];
  notFound: string[];
  summary: { total: number; matched: number; duplicates: number; notFound: number };
}

const FacebookDeletion: React.FC = () => {
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processed, setProcessed] = useState(false);
  const [processResult, setProcessResult] = useState<any>(null);
  const [fileName, setFileName] = useState('');

  // Action config with defaults
  const [markAsDeleted, setMarkAsDeleted] = useState<Set<string>>(new Set(['Email', 'FacebookID']));
  const [cleanFields, setCleanFields] = useState<Set<string>>(new Set(['Token']));
  const [replaceFields, setReplaceFields] = useState<Record<string, string>>({ AccountStatus: 'deleted' });

  const toggleMarkAsDeleted = (field: string) => {
    setMarkAsDeleted((prev) => {
      const next = new Set(prev);
      next.has(field) ? next.delete(field) : next.add(field);
      return next;
    });
  };

  const toggleClean = (field: string) => {
    setCleanFields((prev) => {
      const next = new Set(prev);
      next.has(field) ? next.delete(field) : next.add(field);
      return next;
    });
  };

  const toggleReplace = (field: string, value: string) => {
    setReplaceFields((prev) => {
      const next = { ...prev };
      if (next[field]) {
        delete next[field];
      } else {
        next[field] = value;
      }
      return next;
    });
  };

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setProcessed(false);
    setProcessResult(null);

    const text = await file.text();
    const lines = text
      .split(/[\r\n]+/)
      .map((l) => l.trim())
      .filter((l) => l && /^\d+$/.test(l));

    if (lines.length === 0) {
      toast.error('No valid Facebook IDs found in file');
      return;
    }

    setLoading(true);
    try {
      const res = await fetchWithAuth(`${getAnalyticsApiBaseUrl()}/facebook-deletion/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ facebookIds: lines }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setLookupResult(data);
      toast.success(`Found ${data.summary.matched} users from ${data.summary.total} IDs`);
    } catch (err: any) {
      toast.error('Lookup failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleProcess = async () => {
    if (!lookupResult || lookupResult.matched.length === 0) return;

    const userIds = lookupResult.matched.map((u) => u._id);

    setProcessing(true);
    try {
      const res = await fetchWithAuth(`${getAnalyticsApiBaseUrl()}/facebook-deletion/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userIds,
          actions: {
            markAsDeleted: Array.from(markAsDeleted),
            clean: Array.from(cleanFields),
            replace: replaceFields,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setProcessResult(data);
      setProcessed(true);
      toast.success(`Updated ${data.updated} users successfully`);
    } catch (err: any) {
      toast.error('Process failed: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white'>
        <div className='fixed inset-0 overflow-hidden pointer-events-none'>
          <div className='absolute -top-40 -right-40 w-80 h-80 bg-[#FF9C15]/10 rounded-full blur-3xl' />
          <div className='absolute -bottom-40 -left-40 w-80 h-80 bg-[#F42026]/10 rounded-full blur-3xl' />
        </div>

        <div className='relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
          <h1 className='text-3xl font-bold mb-2'>
            <span className='bg-gradient-to-r from-[#FF9C15] to-[#F42026] bg-clip-text text-transparent'>
              Facebook Account Deletion
            </span>
          </h1>
          <p className='text-gray-400 mb-8'>Upload CSV with Facebook IDs to find and process user deletions</p>

          {/* Upload Section */}
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 mb-6'>
            <div className='flex items-center gap-4'>
              <label className='cursor-pointer px-6 py-3 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-200'>
                <input type='file' accept='.csv,.txt' onChange={handleFileUpload} className='hidden' />
                {loading ? 'Looking up...' : 'Upload CSV'}
              </label>
              {fileName && <span className='text-gray-400'>File: {fileName}</span>}
            </div>
          </div>

          {/* Summary Cards */}
          {lookupResult && (
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-6'>
              <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4'>
                <div className='text-2xl font-bold'>{lookupResult.summary.total}</div>
                <div className='text-gray-400 text-sm'>Total IDs</div>
              </div>
              <div className='bg-white/5 backdrop-blur-sm border border-green-500/30 rounded-xl p-4'>
                <div className='text-2xl font-bold text-green-400'>{lookupResult.summary.matched}</div>
                <div className='text-gray-400 text-sm'>Matched</div>
              </div>
              <div className='bg-white/5 backdrop-blur-sm border border-yellow-500/30 rounded-xl p-4'>
                <div className='text-2xl font-bold text-yellow-400'>{lookupResult.summary.notFound}</div>
                <div className='text-gray-400 text-sm'>Not Found</div>
              </div>
              <div className='bg-white/5 backdrop-blur-sm border border-red-500/30 rounded-xl p-4'>
                <div className='text-2xl font-bold text-red-400'>{lookupResult.summary.duplicates}</div>
                <div className='text-gray-400 text-sm'>Duplicates (Skipped)</div>
              </div>
            </div>
          )}

          {/* Action Configuration */}
          {lookupResult && lookupResult.matched.length > 0 && !processed && (
            <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 mb-6'>
              <h2 className='text-lg font-semibold mb-4'>Action Configuration</h2>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                {/* Mark as Deleted */}
                <div>
                  <h3 className='text-sm font-medium text-orange-400 mb-3'>
                    Mark as Deleted <span className='text-gray-500'>(prefix "deleted-")</span>
                  </h3>
                  {['Email', 'FacebookID'].map((field) => (
                    <label key={field} className='flex items-center gap-2 mb-2 cursor-pointer'>
                      <input
                        type='checkbox'
                        checked={markAsDeleted.has(field)}
                        onChange={() => toggleMarkAsDeleted(field)}
                        className='w-4 h-4 accent-orange-500'
                      />
                      <span className='text-gray-300'>{field}</span>
                    </label>
                  ))}
                </div>

                {/* Clean */}
                <div>
                  <h3 className='text-sm font-medium text-blue-400 mb-3'>
                    Clean <span className='text-gray-500'>(set to empty "")</span>
                  </h3>
                  {['Token'].map((field) => (
                    <label key={field} className='flex items-center gap-2 mb-2 cursor-pointer'>
                      <input
                        type='checkbox'
                        checked={cleanFields.has(field)}
                        onChange={() => toggleClean(field)}
                        className='w-4 h-4 accent-blue-500'
                      />
                      <span className='text-gray-300'>{field}</span>
                    </label>
                  ))}
                </div>

                {/* Replace */}
                <div>
                  <h3 className='text-sm font-medium text-purple-400 mb-3'>
                    Replace <span className='text-gray-500'>(set specific value)</span>
                  </h3>
                  <label className='flex items-center gap-2 mb-2 cursor-pointer'>
                    <input
                      type='checkbox'
                      checked={!!replaceFields['AccountStatus']}
                      onChange={() => toggleReplace('AccountStatus', 'deleted')}
                      className='w-4 h-4 accent-purple-500'
                    />
                    <span className='text-gray-300'>AccountStatus → "deleted"</span>
                  </label>
                </div>
              </div>

              <button
                onClick={handleProcess}
                disabled={processing}
                className='mt-6 px-8 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50'
              >
                {processing
                  ? `Processing ${lookupResult.matched.length} users...`
                  : `Process ${lookupResult.matched.length} Users`}
              </button>
            </div>
          )}

          {/* Process Result */}
          {processResult && (
            <div className='bg-white/5 backdrop-blur-sm border border-green-500/30 rounded-xl p-6 mb-6'>
              <h2 className='text-lg font-semibold text-green-400 mb-2'>Processing Complete</h2>
              <p className='text-gray-300'>
                Updated: <span className='text-green-400 font-bold'>{processResult.updated}</span>
                {processResult.failed?.length > 0 && (
                  <span className='text-red-400 ml-4'>
                    Failed: {processResult.failed.length}
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Matched Users Table */}
          {lookupResult && lookupResult.matched.length > 0 && (
            <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden mb-6'>
              <div className='p-4 border-b border-white/10'>
                <h2 className='text-lg font-semibold'>Matched Users ({lookupResult.matched.length})</h2>
              </div>
              <div className='overflow-x-auto'>
                <table className='w-full text-sm'>
                  <thead>
                    <tr className='border-b border-white/10 text-gray-400'>
                      <th className='px-4 py-3 text-left'>#</th>
                      <th className='px-4 py-3 text-left'>UserName</th>
                      <th className='px-4 py-3 text-left'>FullName</th>
                      <th className='px-4 py-3 text-left'>Email</th>
                      <th className='px-4 py-3 text-left'>FacebookID</th>
                      <th className='px-4 py-3 text-left'>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lookupResult.matched.map((user, i) => (
                      <tr key={user._id} className='border-b border-white/5 hover:bg-white/5'>
                        <td className='px-4 py-2 text-gray-500'>{i + 1}</td>
                        <td className='px-4 py-2'>{user.UserName || '-'}</td>
                        <td className='px-4 py-2'>{user.FullName || '-'}</td>
                        <td className='px-4 py-2 text-gray-300'>{user.Email || '-'}</td>
                        <td className='px-4 py-2 text-gray-400 font-mono text-xs'>{user.FacebookID}</td>
                        <td className='px-4 py-2'>
                          <span
                            className={`px-2 py-0.5 rounded text-xs ${
                              user.AccountStatus === 'active'
                                ? 'bg-green-500/20 text-green-400'
                                : user.AccountStatus === 'deleted'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-gray-500/20 text-gray-400'
                            }`}
                          >
                            {user.AccountStatus || 'unknown'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Duplicates */}
          {lookupResult && lookupResult.duplicates.length > 0 && (
            <div className='bg-white/5 backdrop-blur-sm border border-yellow-500/30 rounded-xl p-6 mb-6'>
              <h2 className='text-lg font-semibold text-yellow-400 mb-3'>
                Duplicates (Skipped) - {lookupResult.duplicates.length}
              </h2>
              {lookupResult.duplicates.map((dup) => (
                <div key={dup.facebookId} className='mb-2 text-sm text-gray-300'>
                  <span className='font-mono text-yellow-400'>{dup.facebookId}</span> — {dup.count} users
                </div>
              ))}
            </div>
          )}

          {/* Not Found */}
          {lookupResult && lookupResult.notFound.length > 0 && (
            <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6'>
              <h2 className='text-lg font-semibold text-gray-400 mb-3'>
                Not Found ({lookupResult.notFound.length})
              </h2>
              <div className='text-xs text-gray-500 font-mono space-y-1'>
                {lookupResult.notFound.map((id) => (
                  <div key={id}>{id}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FacebookDeletion;
